// ──────────────────────────────────────────────────────────
// components/strategy/TradeCard.jsx
// ──────────────────────────────────────────────────────────

import { useState } from "react";

const STRATEGIES = {
  ORB_BREAKOUT: { label: "ORB 15-min", color: "#534AB7" },
  VWAP_SUPERTREND: { label: "VWAP + supertrend", color: "#0F6E56" },
  CPR_MEAN_REVERSION: { label: "CPR mean reversion", color: "#185FA5" },
  GAP_RS_MOMENTUM: { label: "Gap + RS", color: "#D85A30" },
  EMA_CROSS_VOLUME: { label: "EMA cross + vol", color: "#993556" },
  NR7_BREAKOUT: { label: "NR7 breakout", color: "#639922" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

export default function TradeCard({ trade, isOpen, onClose }) {
  const [closing, setClosing] = useState(false);

  const strat = STRATEGIES[trade.strategy] || {
    label: trade.strategy || "—",
    color: "#888",
  };

  const isLong = trade.direction === "BUY";
  const pnl = trade.pnl_points || 0;
  const positive = pnl >= 0;

  // ── Progress bar math ──────────────────────────────────

  const sl = trade.stop_loss || 0;
  const tgt = trade.target_price || 0;
  const range = Math.abs(tgt - sl);
  const currentPrice = trade.entry_price + pnl;

  const progress =
    range > 0
      ? isLong
        ? ((currentPrice - sl) / range) * 100
        : ((sl - currentPrice) / range) * 100
      : 50;

  const entryPct =
    range > 0
      ? isLong
        ? ((trade.entry_price - sl) / range) * 100
        : ((sl - trade.entry_price) / range) * 100
      : 50;

  const clamped = Math.max(0, Math.min(100, progress));

  // ── Close handler ──────────────────────────────────────

  const handleClose = async () => {
    if (!onClose) return;
    setClosing(true);
    await onClose(trade.id, currentPrice);
    setClosing(false);
  };

  // ── Exit reason badge ──────────────────────────────────

  const exitBadge = () => {
    if (!trade.exit_reason) return null;

    const isTarget = trade.exit_reason === "TARGET_HIT";
    const isSL = trade.exit_reason === "SL_HIT";

    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: 500,
          padding: "1px 6px",
          borderRadius: 3,
          background: isTarget ? "#e1f5ee" : isSL ? "#fcebeb" : "#f1f1f1",
          color: isTarget ? "#0f6e56" : isSL ? "#a32d2d" : "#666",
        }}
      >
        {trade.exit_reason.replace(/_/g, " ").toLowerCase()}
      </span>
    );
  };

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${
          isOpen ? (positive ? "#5dcaa5" : "#f09595") : "#e5e5e5"
        }`,
        background: "#fff",
        marginBottom: 8,
      }}
    >
      {/* ── Top row ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
          flexWrap: "wrap",
          gap: 4,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          {/* Strategy badge */}
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: "#fff",
              padding: "2px 8px",
              borderRadius: 4,
              background: strat.color,
            }}
          >
            {strat.label}
          </span>

          {/* Symbol */}
          <span style={{ fontSize: 14, fontWeight: 500, color: "#111" }}>
            {trade.symbol}
          </span>

          {/* Direction */}
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              padding: "1px 5px",
              borderRadius: 3,
              background: isLong ? "#e1f5ee" : "#fcebeb",
              color: isLong ? "#0f6e56" : "#a32d2d",
            }}
          >
            {trade.direction}
          </span>
        </div>

        {/* P&L */}
        <span
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: positive ? "#0f6e56" : "#a32d2d",
          }}
        >
          {pnl > 0 ? "+" : ""}
          {pnl.toFixed(1)} pts
        </span>
      </div>

      {/* ── Data grid ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 4,
          fontSize: 12,
          color: "#888",
        }}
      >
        <div>
          Entry{" "}
          <span style={{ color: "#111", fontWeight: 500 }}>
            {trade.entry_price?.toFixed(2)}
          </span>
        </div>

        {isOpen ? (
          <>
            <div>
              SL{" "}
              <span style={{ color: "#a32d2d", fontWeight: 500 }}>
                {trade.stop_loss?.toFixed(2)}
              </span>
              {trade.trailing_active && (
                <span
                  style={{
                    fontSize: 9,
                    color: "#0f6e56",
                    marginLeft: 3,
                    fontWeight: 500,
                  }}
                >
                  trailed
                </span>
              )}
            </div>
            <div>
              Target{" "}
              <span style={{ color: "#0f6e56", fontWeight: 500 }}>
                {trade.target_price?.toFixed(2)}
              </span>
            </div>
            <div>
              Qty{" "}
              <span style={{ color: "#111", fontWeight: 500 }}>
                {trade.quantity}
              </span>
            </div>
          </>
        ) : (
          <>
            <div>
              Exit{" "}
              <span style={{ color: "#111", fontWeight: 500 }}>
                {trade.exit_price?.toFixed(2)}
              </span>
            </div>
            <div>
              P&L{" "}
              <span style={{ fontWeight: 500, color: "#111" }}>
                ₹{trade.pnl_amount?.toFixed(0)}
              </span>
            </div>
            <div>{exitBadge()}</div>
          </>
        )}
      </div>

      {/* ── Progress bar (open trades only) ── */}
      {isOpen && range > 0 && (
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              position: "relative",
              height: 4,
              background: "#f1f1f1",
              borderRadius: 2,
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                borderRadius: 2,
                width: `${clamped}%`,
                background: positive ? "#5dcaa5" : "#f09595",
                transition: "width 0.5s ease",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: -2,
                left: `${Math.min(97, entryPct)}%`,
                width: 2,
                height: 8,
                background: "#333",
                borderRadius: 1,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 10,
              color: "#aaa",
              marginTop: 4,
            }}
          >
            <span>SL {sl.toFixed(1)}</span>
            <span>Target {tgt.toFixed(1)}</span>
          </div>
        </div>
      )}

      {/* ── Footer (open trades only) ── */}
      {isOpen && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 8,
          }}
        >
          <span style={{ fontSize: 11, color: "#aaa" }}>
            {timeAgo(trade.entry_time)}
          </span>
          <button
            onClick={handleClose}
            disabled={closing}
            style={{
              padding: "3px 10px",
              border: "1px solid #ddd",
              borderRadius: 4,
              background: "#fff",
              cursor: "pointer",
              fontSize: 11,
              color: "#666",
            }}
          >
            {closing ? "Closing..." : "Close"}
          </button>
        </div>
      )}
    </div>
  );
}
